import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { questionTranslations } from '../i18n/translations';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Questions order
const QUESTIONS = [
  'disc_1', 'disc_2', 'disc_3', 'disc_4', 'disc_5',
  'disc_6', 'disc_7', 'disc_8', 'disc_9', 'disc_10',
  'val_1', 'val_2', 'val_3', 'val_4', 'val_5', 'val_6',
  'val_7', 'val_8', 'val_9', 'val_10', 'val_11', 'val_12'
];

export const Test = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [testData, setTestData] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('apollo_test');
    if (!stored) {
      navigate('/');
      return;
    }
    setTestData(JSON.parse(stored));
  }, [navigate]);

  const currentQuestion = QUESTIONS[currentIndex];
  const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
  const isLastQuestion = currentIndex === QUESTIONS.length - 1;

  const handleAnswer = (value) => {
    setAnswers(prev => ({ ...prev, [currentQuestion]: value }));
    
    // Auto-advance after a short delay
    if (!isLastQuestion) {
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
      }, 300);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handleSubmit = async () => {
    if (!testData) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/guest/${testData.guestId}/submit`, {
        test_id: testData.testId,
        sphere: testData.sphere,
        country: testData.country,
        group_name: testData.groupName,
        language: language,
        answers: answers
      });
      navigate('/complete');
    } catch (err) {
      console.error('Error submitting test:', err);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const scaleLabels = [
    { value: 1, label: t('test.scale.1') },
    { value: 2, label: t('test.scale.2') },
    { value: 3, label: t('test.scale.3') },
    { value: 4, label: t('test.scale.4') },
    { value: 5, label: t('test.scale.5') }
  ];

  if (!testData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex justify-between items-center bg-gray-900/80 backdrop-blur-sm border-b border-gray-800">
        <div className="flex items-center gap-3">
          <img src="/apollo-logo.png" alt="Apollo" className="h-8 w-auto" />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm font-dm-sans hidden sm:block">
            {testData.guestName}
          </span>
          <LanguageSwitch />
        </div>
      </header>

      {/* Progress Bar */}
      <div className="fixed top-[72px] left-0 right-0 z-40 px-6 py-3 bg-gray-900/60 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between text-sm text-gray-400 mb-2 font-dm-sans">
            <span>{t('test.question')} {currentIndex + 1} {t('test.of')} {QUESTIONS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2 bg-gray-700" />
        </div>
      </div>

      {/* Main Content */}
      <main className="flex items-center justify-center min-h-screen px-4 pt-36 pb-24">
        <Card className="bg-gray-800/90 border-gray-700 max-w-2xl w-full backdrop-blur-sm shadow-xl" data-testid="question-card">
          <CardContent className="pt-10 pb-10 px-8">
            {/* Question */}
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-orange-500/20 text-orange-500 text-xs font-semibold mb-4 font-manrope">
                {currentQuestion.startsWith('disc') ? 'DISC' : 'VALEURS'}
              </span>
              <h2 className="text-xl sm:text-2xl font-manrope font-bold text-white leading-relaxed" data-testid="question-text">
                {questionTranslations[currentQuestion]?.[language] || currentQuestion}
              </h2>
            </div>

            {/* Scale Buttons */}
            <div className="space-y-3">
              {scaleLabels.map(({ value, label }) => (
                <button
                  key={value}
                  data-testid={`answer-${value}`}
                  onClick={() => handleAnswer(value)}
                  className={`w-full p-4 rounded-lg border transition-all duration-200 flex items-center justify-between group ${
                    answers[currentQuestion] === value
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-gray-900/50 border-gray-700 text-gray-300 hover:border-orange-500/50 hover:bg-gray-800'
                  }`}
                >
                  <span className="font-dm-sans text-left">{label}</span>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center font-manrope font-bold text-sm ${
                    answers[currentQuestion] === value
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-800 text-gray-400 group-hover:text-orange-500'
                  }`}>
                    {answers[currentQuestion] === value ? <Check className="w-4 h-4" /> : value}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Navigation Footer */}
      <footer className="fixed bottom-0 left-0 right-0 z-40 px-6 py-4 bg-gray-900/80 backdrop-blur-sm border-t border-gray-800">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <Button
            data-testid="prev-btn"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            variant="ghost"
            className="text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('test.previous')}
          </Button>

          {isLastQuestion ? (
            <Button
              data-testid="submit-btn"
              onClick={handleSubmit}
              disabled={Object.keys(answers).length < QUESTIONS.length || submitting}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-[0_0_15px_rgba(249,115,22,0.3)] disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ...
                </>
              ) : (
                <>
                  {t('test.submit')}
                  <Check className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              data-testid="next-btn"
              onClick={handleNext}
              disabled={!answers[currentQuestion]}
              className="bg-gray-800 hover:bg-gray-700 text-white border border-gray-700 disabled:opacity-30"
            >
              {t('test.next')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
};
